import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronsUpDown, LogOut, Settings, Loader2, CircleHelp } from 'lucide-react';
import { useLogout } from '@/hooks/useLogout';
import { useTeamContext } from '@/hooks/useTeamContext';
import { useFreelancerProfile } from '@/hooks/queries/useProfile';
import { useDeferredReady } from '@/hooks/useDeferredReady';
import { persistPreferredLocale } from '@/utils/persistPreferredLocale';
import { USER_ROLES } from '@/config/permissions';
import { SvgIcon } from '@/components';
import KYCBadge from '@/components/settings/freelancer/KYCBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { UserProfileAvatar } from '@/components/common/UserProfileAvatar';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

const LANGS = [
  { code: 'en', label: 'EN', flag: 'EN.svg', full: 'English' },
  { code: 'fr', label: 'FR', flag: 'FR.svg', full: 'Français' },
];

const UserMenu = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isNavCompact } = useSidebar();
  const { handleLogout: onLogOut, isLoggingOut } = useLogout();
  const { user, activeTeamRole, effectiveRole, activeTeam, teams } = useTeamContext();
  const isFreelancer = effectiveRole === USER_ROLES.FREELANCER;
  const kycProfileReady = useDeferredReady(800);
  const { data: freelancerProfile } = useFreelancerProfile(isFreelancer && kycProfileReady);

  const resolvedName = useMemo(() => {
    // Only return first name
    const first = user?.first_name ?? user?.firstName;
    if (first) return first;

    // Fallback to email if no first name
    if (user?.email) return user.email;

    // Final fallback
    return '—';
  }, [user]);

  const resolvedRole = useMemo(() => {
    // Afficher le rôle dans l'équipe active si disponible, sinon rôle effectif
    const roleToDisplay = activeTeamRole || effectiveRole;
    if (!roleToDisplay) return t('header.team-member');
    const label = t(`header.${roleToDisplay}`);
    return label || roleToDisplay;
  }, [activeTeamRole, effectiveRole, t]);
  
  // Afficher le nom de l'équipe si plusieurs équipes
  const showTeamInfo = teams.length > 1 && activeTeam;

  const { i18n } = useTranslation();
  const currentLang = i18n.language && i18n.language.startsWith('fr') ? 'fr' : 'en';

  const handleLanguageChange = (langCode) => {
    persistPreferredLocale(langCode);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleHelpCenterClick = () => {
    if (window.Intercom) {
      window.Intercom('showSpace', 'help');
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="default"
              className={cn(
                'min-h-13! py-3! cursor-pointer rounded-lg gap-2 px-3 text-white bg-transparent',
                'hover:bg-white/10 data-[state=open]:bg-white/10 [&:hover_svg]:text-white [&_svg]:text-white/70',
                '[&:hover_.sidebarUserIcon]:rotate-180'
              )}
            >
              <UserProfileAvatar
                userId={user?.id}
                avatar={user?.avatar}
                firstName={user?.first_name ?? user?.firstName}
                lastName={user?.last_name ?? user?.lastName}
                size="md"
                className="h-9 w-9 rounded-full border border-white/20 bg-white/20 shrink-0"
                fallbackClassName="rounded-lg bg-white/20 text-white text-xs"
              />
              <div className="grid flex-1 text-left gap-0.5 text-sm leading-tight min-w-0">
                <span className="truncate font-medium text-white">{resolvedName}</span>
                <span className="truncate text-xs text-white/70 flex items-center gap-1.5">
                  {resolvedRole}
                  {isFreelancer && freelancerProfile && (
                    <KYCBadge freelancer={freelancerProfile} />
                  )}
                </span>
              </div>
              <ChevronsUpDown className={cn('sidebarUserIcon ml-auto size-4 shrink-0 transition-transform duration-300')} />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className={cn(
              'w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg',
              isNavCompact && 'mobile-nav-user-dropdown'
            )}
            align="start"
            side="top"
            sideOffset={4}
          >
            {showTeamInfo && (
              <>
                <DropdownMenuLabel className="text-muted-foreground text-xs">
                  {t('sidebar.teams')}
                </DropdownMenuLabel>
                <DropdownMenuItem disabled className="cursor-default gap-2 p-2 opacity-100">
                  <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                    <Building2 className="size-3.5 shrink-0" />
                  </div>
                  <span className="font-medium">{activeTeam.name}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="gap-2 p-2 cursor-pointer"
                onClick={handleSettingsClick}
              >
                <Settings className="size-4" />
                <span>{t('settings.title')}</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 p-2 cursor-pointer" onClick={handleHelpCenterClick}>
                <CircleHelp className="size-4" />
                <span>{t('sidebar.helpCenter')}</span>
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="gap-2 p-2 cursor-pointer">
                  <SvgIcon name={LANGS.find((l) => l.code === currentLang)?.label || 'EN'} path="i18n" size={20} />
                  <span>{LANGS.find((l) => l.code === currentLang)?.full || 'English'}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className={cn('rounded-lg', isNavCompact && 'mobile-nav-user-dropdown')}>
                  {LANGS.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      className={cn('gap-2 p-2 cursor-pointer', currentLang === lang.code && 'bg-accent')}
                      onClick={() => handleLanguageChange(lang.code)}
                    >
                      <SvgIcon name={lang.label} path="i18n" size={20} />
                      <span>{lang.full}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className={cn('gap-2 p-2 cursor-pointer', isLoggingOut && 'opacity-50 cursor-not-allowed')}
              onClick={onLogOut}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <LogOut className="size-4" />
              )}
              <span>{t('profile.log_out')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

export default UserMenu;
